import { Component, inject } from '@angular/core';

import { ToastService } from '@core/services/toast.service';

/**
 * Renders the ToastService list bottom-right (bottom-centre on phones). Two live regions are
 * always in the DOM, because a screen reader only announces text that changes inside a region
 * that already existed: errors are `alert` (assertive), everything else `status` (polite).
 */
@Component({
  selector: 'app-toast-host',
  template: `
    <div class="toast-stack">
      <div class="toast-region" role="status" aria-live="polite">
        @for (toast of toastService.toasts(); track toast.id) {
          @if (toast.kind !== 'error') {
            <div class="toast" [class]="'toast ' + toast.kind">
              <span class="toast-icon" aria-hidden="true">{{ toast.kind === 'success' ? '✓' : 'i' }}</span>
              <span class="toast-message">{{ toast.message }}</span>
              <button type="button" class="toast-close" aria-label="Meldung schliessen" (click)="toastService.dismiss(toast.id)">×</button>
            </div>
          }
        }
      </div>
      <div class="toast-region" role="alert" aria-live="assertive">
        @for (toast of toastService.toasts(); track toast.id) {
          @if (toast.kind === 'error') {
            <div class="toast error">
              <span class="toast-icon" aria-hidden="true">!</span>
              <span class="toast-message">{{ toast.message }}</span>
              @if (toast.action; as action) {
                <button type="button" class="toast-action" (click)="run(toast.id, action.run)">{{ action.label }}</button>
              }
              <button type="button" class="toast-close" aria-label="Meldung schliessen" (click)="toastService.dismiss(toast.id)">×</button>
            </div>
          }
        }
      </div>
    </div>
  `,
  styles: `
    .toast-stack {
      position: fixed;
      right: var(--s-6);
      bottom: var(--s-6);
      z-index: 1100;
      display: flex;
      flex-direction: column;
      gap: var(--s-2);
      width: min(380px, calc(100vw - 2 * var(--s-4)));
      pointer-events: none;
    }

    .toast-region {
      display: flex;
      flex-direction: column;
      gap: var(--s-2);
    }

    .toast {
      display: flex;
      align-items: flex-start;
      gap: var(--s-3);
      padding: var(--s-3) var(--s-4);
      border-radius: var(--r-md);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-left-width: 4px;
      box-shadow: var(--shadow-lg);
      font-size: 14px;
      pointer-events: auto;
      animation: toast-in var(--t-base);

      &.success { border-left-color: var(--color-success); }
      &.info { border-left-color: var(--color-primary-light); }
      &.error { border-left-color: var(--color-error); }
    }

    .toast-icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      border-radius: var(--r-full);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: #fff;
      background: var(--color-primary-light);
      margin-top: 1px;
    }

    .success .toast-icon { background: var(--color-success); }
    .error .toast-icon { background: var(--color-error); }

    .toast-message {
      flex: 1;
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .toast-action,
    .toast-close {
      flex-shrink: 0;
      color: var(--color-primary);
      font-size: 13px;
    }

    .toast-action {
      text-decoration: underline;
    }

    .toast-close {
      font-size: 18px;
      line-height: 1;
      color: var(--color-text-secondary);
    }

    @keyframes toast-in {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
    }

    @media (max-width: 768px) {
      .toast-stack {
        right: var(--s-4);
        bottom: var(--s-4);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .toast {
        animation: none;
      }
    }
  `,
})
export class ToastHost {
  protected readonly toastService = inject(ToastService);

  protected run(id: number, action: () => void): void {
    this.toastService.dismiss(id);
    action();
  }
}
